import React, { useState, useRef, useEffect } from "react";
import {
    FilesComponent,
} from "../../services/types/ComponentTypes";
import { openImageById, getImageBlob } from "../../services/fileService";

interface FileUploadProps {
    config: FilesComponent;
    value?: string; // base64, URL, or stored image key from API
    onChange?: (name: string, value: string | null) => void;
    onPendingFile?: (name: string, file: File | null) => void; // Pending file is uploaded on form submit
    mode?: string; // Add, Edit, View
}

export const FileUpload: React.FC<FileUploadProps> = ({
    config,
    value,
    onChange,
    onPendingFile,
    mode = "add",
}) => {
    const [fileBase64, setFileBase64] = useState<string | null>(null);
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [fileSize, setFileSize] = useState<number | null>(null);
    const [fileType, setFileType] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(100);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [uploadedKey, setUploadedKey] = useState<string | null>(null);
    const [viewImageLoading, setViewImageLoading] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);
    const [originalSize, setOriginalSize] = useState<number | null>(null);
    const [compressedSize, setCompressedSize] = useState<number | null>(null);
    const fetchedPreviewUrlRef = useRef<string | null>(null); // blob URL for edit-mode image ID preview
    const fileInputRef = useRef<HTMLInputElement>(null);
    const originalFileNameRef = useRef<string | null>(null);
    const pendingPreviewUrlRef = useRef<string | null>(null); // object URL to revoke
    const fileInputId = `fileInput_${config.BindValue || Math.random().toString(36).substr(2, 9)}`;

    const maxFileSize = 5 * 1024 * 1024; // 5MB
    const COMPRESSION_QUALITY = 0.8; // Quality between 0 and 1 (0.8 = 80% quality)
    const MAX_WIDTH = 1920; // Maximum width in pixels
    const MAX_HEIGHT = 1920; // Maximum height in pixels

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
        return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    };

    /**
     * Compress an image file using Canvas API
     * @param file - The image file to compress
     * @returns Promise<File> - The compressed file
     */
    const compressImage = async (file: File): Promise<File> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    // Calculate new dimensions while maintaining aspect ratio
                    let width = img.width;
                    let height = img.height;
                    
                    if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
                        width = width * ratio;
                        height = height * ratio;
                    }

                    // Create canvas and draw image
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    
                    if (!ctx) {
                        reject(new Error('Failed to get canvas context'));
                        return;
                    }

                    // Draw image with better quality settings
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(img, 0, 0, width, height);

                    // Determine output format - PNG doesn't support quality parameter
                    const isPNG = file.type === 'image/png';
                    const outputType = isPNG ? 'image/png' : (file.type || 'image/jpeg');

                    // Convert to blob with compression
                    // Note: PNG doesn't support quality parameter, so we pass undefined for PNG
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('Failed to compress image'));
                                return;
                            }

                            // Ensure blob type matches what we requested
                            const finalBlob = blob.type === outputType 
                                ? blob 
                                : new Blob([blob], { type: outputType });

                            // Create a new File object with the same name and correct type
                            const compressedFile = new File(
                                [finalBlob],
                                file.name,
                                {
                                    type: outputType,
                                    lastModified: Date.now()
                                }
                            );
                            resolve(compressedFile);
                        },
                        outputType,
                        isPNG ? undefined : COMPRESSION_QUALITY // PNG doesn't support quality
                    );
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    useEffect(() => {
        // Normalize value: API may return attachment as object { imageId, storageKey } or as string
        let effectiveValue: string | null = null;
        if (value != null && value !== "") {
            if (typeof value === "string") {
                effectiveValue = value.trim() || null;
            } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
                const v = value as { imageId?: string; storageKey?: string };
                effectiveValue = (v.imageId || v.storageKey || "")?.trim() || null;
            } else {
                effectiveValue = String(value).trim() || null;
            }
        }
        if (effectiveValue) {
            if (typeof effectiveValue === "string" && (effectiveValue.startsWith("http://") || effectiveValue.startsWith("https://"))) {
                setFileUrl(effectiveValue);
                setFileBase64(null);
                setPreviewUrl(effectiveValue);
                setPendingFile(null);
                setUploadedKey(null);
                try {
                    const url = new URL(effectiveValue);
                    const pathSegments = url.pathname.split("/");
                    let filename = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : null;

                    // Decode URL-encoded filename
                    if (filename) {
                        try {
                            filename = decodeURIComponent(filename);
                        } catch {
                            // If decoding fails, use original
                        }
                    }

                    // Use preserved original filename if available, otherwise use extracted filename
                    if (originalFileNameRef.current) {
                        setFileName(originalFileNameRef.current);
                    } else if (filename && filename.includes("_")) {
                        // Try to extract actual filename if it has underscore prefix (common pattern)
                        const parts = filename.split("_");
                        if (parts.length > 1) {
                            // Check if first part looks like an ID/timestamp, if so use the rest
                            const firstPart = parts[0];
                            if (/^\d+$/.test(firstPart) || firstPart.length <= 10) {
                                setFileName(parts.slice(1).join("_"));
                            } else {
                                setFileName(filename); // Use full filename
                            }
                        } else {
                            setFileName(filename);
                        }
                    } else {
                        setFileName(filename || "Uploaded File");
                    }
                    
                    // Detect file type from URL extension
                    if (filename) {
                        const ext = filename.split('.').pop()?.toLowerCase();
                        if (ext === 'pdf') setFileType('application/pdf');
                        else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')) {
                            setFileType(`image/${ext === 'jpg' ? 'jpeg' : ext}`);
                        }
                    }
                    
                    // Try to fetch file size from URL
                    fetch(effectiveValue, { method: 'HEAD' })
                        .then(response => {
                            const contentLength = response.headers.get('content-length');
                            const contentType = response.headers.get('content-type');
                            if (contentLength) {
                                setFileSize(parseInt(contentLength, 10));
                            }
                            if (contentType) {
                                setFileType(contentType);
                            }
                        })
                        .catch(() => {
                            setFileSize(null);
                        });
                } catch {
                    setFileName("Uploaded File");
                    setFileSize(null);
                    setFileType(null);
                }
            } else if (typeof effectiveValue === "string" && effectiveValue.startsWith("data:")) {
                // Base64 / data URL
                setFileBase64(effectiveValue);
                setFileUrl(null);
                setUploadedKey(null);
                setPendingFile(null);
                
                const dataUrlMatch = effectiveValue.match(/data:([^;]+);base64,(.+)/);
                if (dataUrlMatch) {
                    const mimeType = dataUrlMatch[1];
                    setFileType(mimeType);
                    setPreviewUrl(effectiveValue);
                    if (originalFileNameRef.current) {
                        setFileName(originalFileNameRef.current);
                    } else {
                        let fileExt = mimeType.split("/")[1] || "file";
                        if (mimeType === "image/png") fileExt = "png";
                        else if (mimeType === "image/jpeg" || mimeType === "image/jpg") fileExt = "jpg";
                        else if (mimeType === "image/gif") fileExt = "gif";
                        else if (mimeType === "application/pdf") fileExt = "pdf";
                        else if (mimeType === "application/msword") fileExt = "doc";
                        else if (mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") fileExt = "docx";
                        setFileName(`uploaded_file.${fileExt}`);
                    }
                } else {
                    setFileName(originalFileNameRef.current || "uploaded_file");
                    setFileType(null);
                    setPreviewUrl(null);
                }
                const base64Data = effectiveValue.includes(",") ? effectiveValue.split(",")[1] : effectiveValue;
                const estimatedSize = Math.round((base64Data.length * 3) / 4);
                setFileSize(estimatedSize);
            } else {
                // Stored image key from API (short string, not URL, not base64)
                setUploadedKey(typeof effectiveValue === "string" ? effectiveValue : String(effectiveValue));
                setFileBase64(null);
                setFileUrl(null);
                setPreviewUrl(null);
                setPendingFile(null);
                setFileName("Uploaded file");
                setFileSize(null);
                setFileType(null);
            }
        } else {
            setFileBase64(null);
            setFileUrl(null);
            setFileName(null);
            setFileSize(null);
            setFileType(null);
            setPreviewUrl(null);
            setPendingFile(null);
            setUploadedKey(null);
            setIsCompressing(false);
            setOriginalSize(null);
            setCompressedSize(null);
        }
    }, [value]);

    useEffect(() => {
        return () => {
            if (pendingPreviewUrlRef.current) {
                URL.revokeObjectURL(pendingPreviewUrlRef.current);
                pendingPreviewUrlRef.current = null;
            }
        };
    }, []);

    // In edit mode when value is image ID: fetch image for preview thumbnail
    useEffect(() => {
        if (!uploadedKey || fileUrl || fileBase64) {
            if (fetchedPreviewUrlRef.current) {
                URL.revokeObjectURL(fetchedPreviewUrlRef.current);
                fetchedPreviewUrlRef.current = null;
            }
            return;
        }
        let cancelled = false;
        getImageBlob(uploadedKey)
            .then((blob) => {
                if (cancelled) return;
                if (fetchedPreviewUrlRef.current) {
                    URL.revokeObjectURL(fetchedPreviewUrlRef.current);
                }
                const url = URL.createObjectURL(blob);
                fetchedPreviewUrlRef.current = url;
                setPreviewUrl(url);
                setFileType(blob.type || "image/jpeg");
            })
            .catch(() => {
                if (!cancelled) {
                    setPreviewUrl(null);
                    setFileType(null);
                }
            });
        return () => {
            cancelled = true;
            if (fetchedPreviewUrlRef.current) {
                URL.revokeObjectURL(fetchedPreviewUrlRef.current);
                fetchedPreviewUrlRef.current = null;
            }
        };
    }, [uploadedKey, fileUrl, fileBase64]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > maxFileSize) {
            alert("File size exceeds 5MB limit");
            return;
        }

        if (pendingPreviewUrlRef.current) {
            URL.revokeObjectURL(pendingPreviewUrlRef.current);
            pendingPreviewUrlRef.current = null;
        }

        originalFileNameRef.current = file.name;
        setFileType(file.type);
        setFileBase64(null);
        setFileUrl(null);
        setUploadedKey(null);
        setOriginalSize(null);
        setCompressedSize(null);

        // Check if file is an image that can be compressed
        const isImage = file.type.startsWith("image/") && 
                       (file.type === "image/jpeg" || file.type === "image/jpg" || 
                        file.type === "image/png" || file.type === "image/webp");

        if (isImage) {
            setIsCompressing(true);
            try {
                const compressedFile = await compressImage(file);
                setOriginalSize(file.size);
                setCompressedSize(compressedFile.size);
                setFileName(file.name);
                setFileSize(compressedFile.size);
                setFileType(compressedFile.type); // Ensure file type is set correctly after compression
                setPendingFile(compressedFile);
                onPendingFile?.(config.name || config.BindValue || "", compressedFile);

                const objUrl = URL.createObjectURL(compressedFile);
                pendingPreviewUrlRef.current = objUrl;
                setPreviewUrl(objUrl);
            } catch (error) {
                console.error("Compression failed:", error);
                // Fallback to original file if compression fails
                setFileName(file.name);
                setFileSize(file.size);
                setFileType(file.type);
                setPendingFile(file);
                onPendingFile?.(config.name || config.BindValue || "", file);

                const objUrl = URL.createObjectURL(file);
                pendingPreviewUrlRef.current = objUrl;
                setPreviewUrl(objUrl);
            } finally {
                setIsCompressing(false);
            }
        } else {
            // For non-image files, use as-is
            setFileName(file.name);
            setFileSize(file.size);
            setPendingFile(file);
            onPendingFile?.(config.name || config.BindValue || "", file);

            const objUrl = URL.createObjectURL(file);
            pendingPreviewUrlRef.current = objUrl;
            if (file.type === "application/pdf") {
                setPreviewUrl(objUrl);
            } else {
                setPreviewUrl(null);
            }
        }

        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (pendingPreviewUrlRef.current) {
            URL.revokeObjectURL(pendingPreviewUrlRef.current);
            pendingPreviewUrlRef.current = null;
        }
        setFileBase64(null);
        setFileName(null);
        setFileUrl(null);
        setFileSize(null);
        setFileType(null);
        setPreviewUrl(null);
        setPendingFile(null);
        setUploadedKey(null);
        setIsCompressing(false);
        setOriginalSize(null);
        setCompressedSize(null);
        originalFileNameRef.current = null;
        onPendingFile?.(config.name || config.BindValue || "", null);
        onChange?.(config.name || config.BindValue || "", null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };
    
    // Get file icon based on file type
    const getFileIcon = () => {
        if (!fileType) {
            return (
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
            );
        }
        
        if (fileType.startsWith('image/')) {
            return (
                <svg className="w-8 h-8 text-blue-400 dark:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            );
        }
        
        if (fileType === 'application/pdf') {
            return (
                <svg className="w-8 h-8 text-red-400 dark:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            );
        }
        
        return (
            <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        );
    };

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        const source = fileUrl || previewUrl;
        if (!source) return;
        if (fileUrl) {
            window.open(fileUrl, "_blank");
            return;
        }
        // Download from base64/data URL (e.g. uploaded image)
        try {
            const link = document.createElement("a");
            link.href = source;
            link.download = fileName || "download";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch {
            window.open(source, "_blank");
        }
    };

    const handleView = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsViewModalOpen(true);
        setZoomLevel(100);
    };

    const handleViewByImageId = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!uploadedKey) return;
        setViewImageLoading(true);
        try {
            await openImageById(uploadedKey);
        } catch (err: any) {
            alert(err?.message || "Failed to open image.");
        } finally {
            setViewImageLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsViewModalOpen(false);
        setZoomLevel(100);
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 25, 300));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 25, 25));
    };

    const handleResetZoom = () => {
        setZoomLevel(100);
    };

    const isViewMode = mode?.toLowerCase() === "view" || config.disabled || (config as any).IsReadOnly;
    const canView = !!(fileUrl || previewUrl) && (fileType?.startsWith("image/") || fileType === "application/pdf" || !!fileUrl);
    const canViewByImageId = !!uploadedKey; // Edit mode: value is image ID, open via API in new tab (show eye whenever we have uploadedKey)
    const showEyeIcon = canView || canViewByImageId; // Show eye when form has file (edit mode) - by URL/preview or by image ID

    return (
        <div className="w-full px-2">
            <style>
                {`
          .file-upload-container {
              position: relative;
              width: 95%;
              margin: 0;
          }

          .file-upload-input {
              position: absolute;
              opacity: 0;
              width: 0;
              height: 0;
              pointer-events: none;
              display: flex;
          }

          .file-upload-label {
              display: flex;
              justify-content: center;
              gap: 25px;
              width: 90%;
              border: 2px solid #cbd5e0;
              border-radius: 8px;
              background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
              cursor: pointer;
              transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
              text-align: center;
              position: relative;
              overflow: hidden;
              padding: 5px;
              align-items: center;
          }

          .dark .file-upload-label {
              border-color: #4b5563;
              background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
          }

          .upload-backdrop {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 70%);
              opacity: 0;
              transition: opacity 0.4s ease;
          }

          .file-upload-label:hover .upload-backdrop {
              opacity: 1;
          }

          .upload-animation {
              display: flex;
              justify-content: center;
              align-items: center;
          }

          .cloud-wrapper {
              position: relative;
              width: 50px;
              height: 50px;
              display: flex;
              align-items: center;
              justify-content: center;
          }

          .cloud-icon {
              width: 40px;
              height: 40px;
              color: var(--color-primary-500, #35ba5b);
              filter: drop-shadow(0 4px 12px rgba(59, 130, 246, 0.3));
              transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .upload-arrow {
              position: absolute;
              width: 15px;
              height: 15px;
              color: #6c757d;
          }

          .upload-text {
              position: relative;
              z-index: 1;
              display: flex;
              align-items: center;
              text-align: left;
          }

          .upload-title {
              font-size: 12px;
              font-weight: 700;
              color: #1e293b;
              margin: 0 0 5px 0;
              letter-spacing: -0.02em;
          }

          .dark .upload-title {
              color: #e5e7eb;
          }

          .upload-limit {
              font-size: 12px;
              color: #94a3b8;
              margin: 0;
              font-weight: 400;
          }

          .dark .upload-limit {
              color: #9ca3af;
          }

          .file-upload-label:hover {
              border-color: var(--color-primary-500, #35ba5b);
              box-shadow: 0 12px 40px rgb(126 255 150 / 0%);
          }

          .file-upload-label.view-mode-disabled {
              cursor: not-allowed;
              opacity: 0.6;
              pointer-events: none;
          }

          .uploaded-file-card {
              background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
              border: 1px solid #e2e8f0;
              border-radius: 16px;
              width: 90%;
              padding: 10px;
              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
              transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              position: relative;
              overflow: hidden;
          }

          .dark .uploaded-file-card {
              background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
              border-color: #4b5563;
              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          }

          .file-card-header {
              display: flex;
              align-items: center;
              gap: 15px;
          }

          .file-icon-container {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 48px;
              height: 48px;
              background-color: #f1f5f9;
              border-radius: 12px;
          }

          .dark .file-icon-container {
              background-color: #374151;
          }

          .file-details {
              flex: 1;
              min-width: 0;
          }

          .file-title {
              font-size: 12px;
              font-weight: 700;
              color: #1e293b;
              margin: 0 0 4px 0;
              word-break: break-all;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
          }

          .dark .file-title {
              color: #e5e7eb;
          }

          .file-meta {
              display: flex;
              align-items: center;
              gap: 10px;
              flex-wrap: wrap;
          }

          .status-badge {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              font-size: 11px;
              font-weight: 600;
              padding: 4px 10px;
              border-radius: 8px;
          }

          .status-badge.success {
              background: #d1fae5;
              color: #065f46;
              border: 1px solid #6ee7b7;
          }

          .status-badge.pending {
              background: #dbeafe;
              color: #1e40af;
              border: 1px solid #93c5fd;
          }

          .status-dot {
              width: 6px;
              height: 6px;
              border-radius: 50%;
          }

          .status-badge.success .status-dot { background: var(--color-primary-500, #35ba5b); }
          .status-badge.pending .status-dot { background: #3b82f6; }

          .action-button {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 32px;
              height: 32px;
              border: none;
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s;
              color: white;
          }

          .action-button:hover { transform: translateY(-2px); }

          .action-button.view { background: #3b82f6; }
          .action-button.download { background: var(--color-primary-500, #35ba5b); }
          .action-button.delete { background: #ef4444; }
        `}
            </style>

            <div className="group relative">
                {!fileBase64 && !fileUrl && !pendingFile && !uploadedKey ? (
                    <>
                        {config.label && (
                            <label className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                {config.label}
                                {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
                            </label>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            id={fileInputId}
                            onChange={handleFileChange}
                            disabled={isViewMode}
                            accept="*"
                        />
                        <label
                            htmlFor={fileInputId}
                            className={`flex flex-col items-center justify-center w-full h-[104px] border border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 hover:border-primary-400 dark:hover:border-primary-500 transition-all duration-200 ${
                                isViewMode ? "opacity-60 cursor-not-allowed pointer-events-none" : ""
                            }`}
                        >
                            <div className="flex flex-col items-center justify-center py-3">
                                <svg
                                    className="w-6 h-6 mb-1 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                    />
                                </svg>
                                <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-primary-700 dark:group-hover:text-primary-400">
                                    <span className="font-medium">Click to upload</span>
                                </p>
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">MAX 5MB</p>
                            </div>
                        </label>
                    </>
                ) : (
                    <div className="w-full">
                        {config.label && (
                            <label className="block mb-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                {config.label}
                                {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
                            </label>
                        )}
                        <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                            {/* File Preview Section - Fixed Height with eye icon to view */}
                            <div className="mb-4 h-48 relative flex items-center justify-center overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                                {previewUrl && (fileType?.startsWith('image/') || fileType === 'application/pdf') ? (
                                    <>
                                        {fileType?.startsWith('image/') ? (
                                            <img 
                                                src={previewUrl} 
                                                alt={fileName || "Preview"} 
                                                className="max-h-full max-w-full object-contain"
                                            />
                                        ) : fileType === 'application/pdf' ? (
                                            <iframe
                                                src={previewUrl}
                                                className="w-full h-full rounded-lg"
                                                title={fileName || "PDF Preview"}
                                            />
                                        ) : null}
                                    </>
                                ) : (
                                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                                        {getFileIcon()}
                                    </div>
                                )}
                                {/* Eye icon overlay - view image (edit mode: show when form has file) */}
                                {showEyeIcon && (
                                    <button
                                        type="button"
                                        onClick={(e) => canViewByImageId ? handleViewByImageId(e) : handleView(e)}
                                        disabled={canViewByImageId && viewImageLoading}
                                        className="absolute top-2 right-2 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary-500/90 hover:bg-primary-600 text-white shadow-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                        title={canViewByImageId ? "View image (opens in new tab)" : "View image"}
                                    >
                                        {canViewByImageId && viewImageLoading ? (
                                            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </div>
                            
                            {/* Compression Loading Indicator */}
                            {isCompressing && (
                                <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <svg className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                            Compressing image...
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* File Name - Exact name display */}
                            {fileName && (
                                <div className="mb-2">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">File Name:</p>
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-all">
                                        {fileName}
                                    </h4>
                                </div>
                            )}
                            
                            {/* File Size with Compression Info */}
                            {fileSize && (
                                <div className="mb-3">
                                    {originalSize && compressedSize && originalSize > compressedSize ? (
                                        <div className="space-y-1">
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Original Size: <span className="font-medium line-through">{formatFileSize(originalSize)}</span>
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Compressed Size: <span className="font-medium text-green-600 dark:text-green-400">{formatFileSize(compressedSize)}</span>
                                            </p>
                                            <p className="text-xs font-medium text-green-600 dark:text-green-400">
                                                Saved: {formatFileSize(originalSize - compressedSize)} ({Math.round(((originalSize - compressedSize) / originalSize) * 100)}%)
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Size: <span className="font-medium">{formatFileSize(fileSize)}</span>
                                        </p>
                                    )}
                                </div>
                            )}
                            {uploadedKey && (
                                <div className="mb-3">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Uploaded</p>
                                    <p className="text-sm font-mono text-gray-700 dark:text-gray-300 break-all">{uploadedKey}</p>
                                </div>
                            )}
                            {/* Action Buttons - View, Download, Remove (images upload on form submit) */}
                            <div className="flex flex-wrap gap-2 justify-end items-center">
                                {showEyeIcon && (
                                    <button
                                        type="button"
                                        onClick={(e) => canViewByImageId ? handleViewByImageId(e) : handleView(e)}
                                        disabled={canViewByImageId && viewImageLoading}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/30 border border-primary-300 dark:border-primary-700 text-primary-600 dark:text-primary-400 rounded-md text-xs font-medium hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                        title={canViewByImageId ? "View image (opens in new tab)" : "View file"}
                                    >
                                        {canViewByImageId && viewImageLoading ? (
                                            "Opening..."
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                                View
                                            </>
                                        )}
                                    </button>
                                )}
                                {(fileUrl || previewUrl) && (
                                    <button
                                        type="button"
                                        onClick={handleDownload}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-700 text-green-600 dark:text-green-400 rounded-md text-xs font-medium hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                                        title="Download file"
                                    >
                                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        Download
                                    </button>
                                )}
                                {!isViewMode && (
                                    <button
                                        type="button"
                                        onClick={handleDelete}
                                        className="px-3 py-1.5 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-md text-xs font-medium hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* View Modal with Zoom Controls */}
            {isViewModalOpen && (fileUrl || previewUrl) && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75 dark:bg-opacity-85" onClick={handleCloseModal}>
                    <div className="relative w-[90vw] h-[90vh] max-w-7xl max-h-[90vh] bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                    {fileName || "File Preview"}
                                </h3>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                                {/* Zoom Controls */}
                                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg px-2 py-1">
                                    <button
                                        onClick={handleZoomOut}
                                        disabled={zoomLevel <= 25}
                                        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                                        title="Zoom Out"
                                    >
                                        <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                                        </svg>
                                    </button>
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 min-w-[3rem] text-center">
                                        {zoomLevel}%
                                    </span>
                                    <button
                                        onClick={handleZoomIn}
                                        disabled={zoomLevel >= 300}
                                        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                                        title="Zoom In"
                                    >
                                        <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={handleResetZoom}
                                        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors ml-1"
                                        title="Reset Zoom"
                                    >
                                        <svg className="w-4 h-4 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                    </button>
                                </div>
                                {/* Close Button */}
                                <button
                                    onClick={handleCloseModal}
                                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    title="Close"
                                >
                                    <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Modal Content - Scrollable with Zoom */}
                        <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
                            <div className="flex items-center justify-center h-full w-full" style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'center' }}>
                                {fileType?.startsWith('image/') ? (
                                    <img 
                                        src={fileUrl || previewUrl || undefined} 
                                        alt={fileName || "Preview"} 
                                        className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                                    />
                                ) : fileType === 'application/pdf' ? (
                                    <iframe
                                        src={fileUrl || previewUrl || undefined}
                                        className="w-full h-full min-h-[600px] rounded-lg shadow-lg border border-gray-300 dark:border-gray-600"
                                        title={fileName || "PDF Preview"}
                                    />
                                ) : fileUrl ? (
                                    <iframe
                                        src={fileUrl}
                                        className="w-full h-full min-h-[600px] rounded-lg shadow-lg border border-gray-300 dark:border-gray-600"
                                        title={fileName || "File Preview"}
                                    />
                                ) : (
                                    <div className="flex flex-col items-center justify-center p-8">
                                        <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-4">
                                            {getFileIcon()}
                                        </div>
                                        <p className="text-gray-600 dark:text-gray-400">Preview not available</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
